import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";

interface HeroProps {
  onLaunch: () => void;
}

// ──────────────────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────────────────
function useTick(intervalMs = 40) {
  const [, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

function useLerp(target: number, speed = 0.06) {
  const [v, setV] = useState(target * 0.4);
  const vRef = useRef(target * 0.4);
  useEffect(() => {
    let raf = 0;
    const step = () => {
      const cur = vRef.current + (target - vRef.current) * speed;
      vRef.current = Math.abs(target - cur) < 0.01 ? target : cur;
      setV(vRef.current);
      if (vRef.current !== target) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, speed]);
  return v;
}

function useWordCycle<T>(words: T[], intervalMs = 2400): [T, number] {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % words.length), intervalMs);
    return () => clearInterval(id);
  }, [words.length, intervalMs]);
  return [words[i], i];
}

// ──────────────────────────────────────────────────────────────────
// Ambient background — particles, grid, scanlines, radial glow
// ──────────────────────────────────────────────────────────────────
function AmbientBG() {
  useTick(50);
  const t = Date.now() / 1000;
  const particles = [];
  for (let i = 0; i < 14; i++) {
    const seed = i * 37;
    const x = (seed * 13) % 100;
    const y = (Math.sin(t * 0.2 + i) * 0.5 + 0.5) * 100;
    const op = 0.15 + 0.25 * Math.abs(Math.sin(t * 0.4 + i));
    const isOrange = i % 5 === 0;
    particles.push(
      <div
        key={i}
        className="absolute w-[3px] h-[3px] rounded-full"
        style={{
          left: x + "%",
          top: y + "%",
          background: isOrange ? "var(--color-brand)" : "var(--color-text-tertiary)",
          opacity: op,
          boxShadow: isOrange ? "0 0 8px var(--color-brand)" : "none",
        }}
      />
    );
  }
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 800px 600px at 70% 50%, rgba(255,107,0,0.10), transparent 60%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at 60% 50%, #000 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at 60% 50%, #000 30%, transparent 80%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 3px)",
        }}
      />
      {particles}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// HUD chip + icons
// ──────────────────────────────────────────────────────────────────
function HudChip({
  icon,
  label,
  accent = false,
}: {
  icon: React.ReactNode;
  label: string;
  accent?: boolean;
}) {
  return (
    <div
      className="font-mono inline-flex items-center gap-1.5 px-2 py-1 rounded-[4px] backdrop-blur-sm text-[10px] tracking-[0.1em] font-semibold"
      style={{
        background: "rgba(0,0,0,0.55)",
        border: accent ? "1px solid rgba(255,107,0,0.3)" : "1px solid rgba(255,255,255,0.06)",
        color: accent ? "var(--color-brand)" : "var(--color-text-primary)",
      }}
    >
      <span className="inline-flex opacity-85">{icon}</span>
      {label}
    </div>
  );
}

const TimerIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
    <path d="M12 9v4l2.5 2.5M9 2h6M12 5V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-7 7-13a7 7 0 10-14 0c0 6 7 13 7 13z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const AmmoIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
    <rect x="6" y="3" width="5" height="9" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="6" y="13" width="5" height="8" stroke="currentColor" strokeWidth="2" />
    <rect x="13" y="6" width="5" height="9" rx="1" stroke="currentColor" strokeWidth="2" />
    <rect x="13" y="16" width="5" height="5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────
// Live duel card
// ──────────────────────────────────────────────────────────────────
function DuelCard() {
  useTick(40);
  const t = Date.now() / 1000;
  const hpA = Math.max(8, 60 + Math.sin(t * 0.6) * 30);
  const hpB = Math.max(8, 50 + Math.cos(t * 0.5 + 1) * 28);
  const pot = useLerp(189.5, 0.04);
  const round = (Math.floor(t / 4) % 7) + 1;

  const camX = Math.sin(t * 0.4) * 6;
  const crossPulse = 0.85 + Math.abs(Math.sin(t * 1.2)) * 0.15;
  const muzzle = Math.floor(t * 1.5) % 5 === 0;
  const livePulseOp = 0.5 + Math.abs(Math.sin(t * 2)) * 0.5;
  const killfeedOp = 0.6 + Math.sin(t * 0.8) * 0.4;
  const ammoMain = 30 - (Math.floor(t * 2) % 30);
  const timer = 45 - (Math.floor(t * 1) % 45);
  const opponentX = 274 + Math.sin(t * 0.8) * 8 + camX * 0.9;
  const smokeRx = 14 + Math.sin(t) * 2;
  const smokeOp = 0.25 + Math.sin(t * 0.6) * 0.05;

  return (
    <div
      className="relative w-full max-w-[460px] rounded-[14px] overflow-hidden"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-line)",
        boxShadow:
          "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,107,0,0.06) inset",
      }}
    >
      <div
        className="h-0.5"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--color-brand) 20%, var(--color-brand) 80%, transparent)",
        }}
      />

      {/* Status strip */}
      <div
        className="font-mono flex items-center justify-between px-4 py-2.5 text-[10px] tracking-[0.14em]"
        style={{
          borderBottom: "1px solid var(--color-line)",
          color: "var(--color-text-tertiary)",
        }}
      >
        <span className="flex items-center gap-1.5">
          <span
            className="w-[7px] h-[7px] rounded-full"
            style={{
              background: "#ff3030",
              boxShadow: "0 0 8px #ff3030",
              opacity: livePulseOp,
            }}
          />
          LIVE · FPS ARENA
        </span>
        <span>
          ROUND <span style={{ color: "var(--color-text-primary)" }}>{String(round).padStart(2, "0")}</span> / 13
        </span>
        <span>1V1 · DUST CRYPTO</span>
      </div>

      {/* Match viewport */}
      <div
        className="relative h-[220px] overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 40%, #2a1d12 0%, #14100a 50%, #050403 100%)",
        }}
      >
        <svg viewBox="0 0 460 220" width="100%" height="100%" className="absolute inset-0" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="hero-sky" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#3a2818" />
              <stop offset="0.5" stopColor="#1c1208" />
              <stop offset="1" stopColor="#0a0703" />
            </linearGradient>
            <linearGradient id="hero-floor" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#241810" />
              <stop offset="1" stopColor="#070403" />
            </linearGradient>
            <radialGradient id="hero-hotspot" cx="0.5" cy="0.45" r="0.5">
              <stop offset="0" stopColor="rgba(255,150,80,0.18)" />
              <stop offset="1" stopColor="transparent" />
            </radialGradient>
            <filter id="hero-soft">
              <feGaussianBlur stdDeviation="0.6" />
            </filter>
            <radialGradient id="hero-vignette" cx="0.5" cy="0.5" r="0.7">
              <stop offset="0.5" stopColor="transparent" />
              <stop offset="1" stopColor="rgba(0,0,0,0.65)" />
            </radialGradient>
          </defs>

          <rect x="0" y="0" width="460" height="135" fill="url(#hero-sky)" />
          <rect x="0" y="0" width="460" height="220" fill="url(#hero-hotspot)" />

          {/* Far buildings */}
          <g opacity="0.5">
            <rect x="-10" y="80" width="60" height="60" fill="#150e08" />
            <rect x="50" y="70" width="50" height="70" fill="#1a1109" />
            <rect x="380" y="78" width="70" height="62" fill="#150e08" />
            <rect x="430" y="88" width="40" height="52" fill="#1a1109" />
          </g>

          {/* Mid buildings parallax */}
          <g transform={`translate(${camX * 0.4}, 0)`}>
            <rect x="20" y="92" width="90" height="56" fill="#1f140c" />
            <rect x="110" y="80" width="40" height="68" fill="#26190f" />
            <rect x="320" y="86" width="80" height="62" fill="#1f140c" />
            <rect x="34" y="106" width="6" height="8" fill="#5a3416" opacity="0.55" />
            <rect x="48" y="106" width="6" height="8" fill="#5a3416" opacity="0.55" />
            <rect x="68" y="118" width="6" height="8" fill="#5a3416" opacity="0.4" />
            <rect x="338" y="100" width="6" height="8" fill="#5a3416" opacity="0.55" />
            <rect x="354" y="116" width="6" height="8" fill="#5a3416" opacity="0.4" />
            <rect x="374" y="100" width="6" height="8" fill="#5a3416" opacity="0.55" />
          </g>

          <rect x="0" y="148" width="460" height="72" fill="url(#hero-floor)" />

          {[155, 165, 178, 195, 215].map((y, i) => (
            <line key={i} x1="0" x2="460" y1={y} y2={y} stroke="#3a2614" strokeWidth="0.5" opacity={0.15 + i * 0.06} />
          ))}
          <line x1="230" y1="148" x2="100" y2="220" stroke="#3a2614" strokeWidth="0.5" opacity="0.4" />
          <line x1="230" y1="148" x2="360" y2="220" stroke="#3a2614" strokeWidth="0.5" opacity="0.4" />

          {/* Mid crate */}
          <g transform={`translate(${camX * 0.7}, 0)`}>
            <rect x="184" y="128" width="58" height="32" fill="#2a1c10" stroke="#4a311a" strokeWidth="1" />
            <line x1="184" y1="128" x2="195" y2="120" stroke="#4a311a" strokeWidth="0.8" />
            <line x1="242" y1="128" x2="253" y2="120" stroke="#4a311a" strokeWidth="0.8" />
            <line x1="195" y1="120" x2="253" y2="120" stroke="#4a311a" strokeWidth="0.8" />
            <rect x="184" y="160" width="58" height="2" fill="#0a0603" />
          </g>

          {/* Smoke */}
          <ellipse cx="280" cy="125" rx={smokeRx} ry="6" fill="#3a2614" opacity={smokeOp} />

          {/* Opponent silhouette */}
          <g transform={`translate(${opponentX}, 116)`} filter="url(#hero-soft)">
            <ellipse cx="0" cy="20" rx="6" ry="1.5" fill="#000" opacity="0.5" />
            <path d="M-3 -2 L3 -2 L4 14 L-4 14 Z" fill="#1a0e06" />
            <circle cx="0" cy="-7" r="3" fill="#1a0e06" />
            <path d="M-3 -2 L-4 14" stroke="#ff6b00" strokeWidth="0.6" opacity="0.6" />
          </g>

          {/* Tracer */}
          {muzzle && (
            <g>
              <line x1="320" y1="220" x2="232" y2="118" stroke="#ffd28a" strokeWidth="1" opacity="0.7" strokeLinecap="round" />
              <line x1="320" y1="220" x2="232" y2="118" stroke="#fff" strokeWidth="0.4" opacity="0.9" />
            </g>
          )}

          <rect x="0" y="0" width="460" height="220" fill="url(#hero-vignette)" />
        </svg>

        {/* Crosshair */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: "50%",
            top: "48%",
            transform: `translate(-50%, -50%) scale(${crossPulse})`,
          }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="1.2" fill="#5ee08f" />
            <path d="M16 6v5M16 21v5M6 16h5M21 16h5" stroke="#5ee08f" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="16" cy="16" r="9" stroke="#5ee08f" strokeWidth="0.5" opacity="0.3" />
          </svg>
        </div>

        {/* HUD chips */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <HudChip icon={<TimerIcon />} label={`01:${String(timer).padStart(2, "0")}`} />
          <HudChip icon={<MapPinIcon />} label="A-SITE" />
        </div>
        <div className="absolute top-3 right-3">
          <HudChip icon={<AmmoIcon />} label={`${ammoMain} · 90`} accent />
        </div>

        {/* Weapon name */}
        <div
          className="font-mono absolute bottom-2.5 right-3 px-2 py-1 rounded-[4px] backdrop-blur-sm text-[10px] tracking-[0.18em] font-semibold flex items-center gap-1.5"
          style={{
            background: "rgba(0,0,0,0.55)",
            border: "1px solid rgba(255,107,0,0.25)",
            color: "var(--color-brand)",
          }}
        >
          <span className="w-1 h-1 rounded-full" style={{ background: "var(--color-brand)" }} />
          AK-47
        </div>

        {/* Killfeed */}
        <div
          className="font-mono absolute bottom-2.5 left-3 px-2 py-1 rounded-[4px] backdrop-blur-sm text-[9px] tracking-[0.08em] flex items-center gap-1.5"
          style={{
            background: "rgba(0,0,0,0.55)",
            color: "var(--color-text-secondary)",
            opacity: killfeedOp,
          }}
        >
          <span style={{ color: "var(--color-brand)", fontWeight: 600 }}>kZero</span>
          <span style={{ color: "var(--color-text-tertiary)" }}>━▶</span>
          <span>v3ng3r</span>
        </div>

        {/* Scanline */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 3px)",
          }}
        />
      </div>

      {/* Players & pot */}
      <div className="px-[18px] py-4" style={{ borderBottom: "1px solid var(--color-line)" }}>
        <div className="flex items-center justify-between gap-3.5">
          {/* P1 */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <div
                className="w-[26px] h-[26px] rounded-full"
                style={{
                  background: "linear-gradient(135deg, #ff8a50, #e8551a)",
                  border: "1.5px solid var(--color-brand)",
                }}
              />
              <div className="flex flex-col">
                <span className="text-[12px] font-semibold">kZero</span>
                <span className="font-mono text-[9px]" style={{ color: "var(--color-text-tertiary)" }}>
                  4fKp…2c44
                </span>
              </div>
            </div>
            <div
              className="h-[6px] rounded-full overflow-hidden"
              style={{ background: "var(--color-elevated)" }}
            >
              <div
                className="h-full transition-all duration-75"
                style={{
                  width: hpA + "%",
                  background: hpA > 30 ? "var(--color-live)" : "var(--color-negative)",
                  boxShadow: `0 0 8px ${hpA > 30 ? "var(--color-live)" : "var(--color-negative)"}`,
                }}
              />
            </div>
            <div
              className="font-mono text-[9px] mt-1 tracking-[0.1em]"
              style={{ color: "var(--color-text-tertiary)" }}
            >
              HP{" "}
              <span style={{ color: hpA > 30 ? "var(--color-live)" : "var(--color-negative)" }}>
                {Math.round(hpA)}
              </span>{" "}
              · K3 / D2
            </div>
          </div>

          {/* Pot */}
          <div
            className="flex-none px-3.5 py-2.5 rounded-[10px] text-center min-w-[110px]"
            style={{
              background: "rgba(255,107,0,0.08)",
              border: "1px solid rgba(255,107,0,0.3)",
            }}
          >
            <div
              className="font-mono text-[9px] tracking-[0.16em]"
              style={{ color: "var(--color-brand)" }}
            >
              POT
            </div>
            <div className="text-[20px] font-extrabold tracking-[-0.02em] mt-0.5">
              ${pot.toFixed(2)}
            </div>
            <div className="font-mono text-[9px] mt-0.5" style={{ color: "var(--color-text-tertiary)" }}>
              USDT
            </div>
          </div>

          {/* P2 */}
          <div className="flex-1 text-right">
            <div className="flex items-center gap-2 mb-1.5 justify-end">
              <div className="flex flex-col items-end">
                <span className="text-[12px] font-semibold">v3ng3r</span>
                <span className="font-mono text-[9px]" style={{ color: "var(--color-text-tertiary)" }}>
                  Cv39…53e5
                </span>
              </div>
              <div
                className="w-[26px] h-[26px] rounded-full"
                style={{
                  background: "linear-gradient(135deg, #444, #222)",
                  border: "1.5px solid var(--color-line-emphasis)",
                }}
              />
            </div>
            <div
              className="h-[6px] rounded-full overflow-hidden flex justify-end"
              style={{ background: "var(--color-elevated)" }}
            >
              <div
                className="h-full transition-all duration-75"
                style={{
                  width: hpB + "%",
                  background: hpB > 30 ? "var(--color-live)" : "var(--color-negative)",
                  boxShadow: `0 0 8px ${hpB > 30 ? "var(--color-live)" : "var(--color-negative)"}`,
                }}
              />
            </div>
            <div
              className="font-mono text-[9px] mt-1 tracking-[0.1em]"
              style={{ color: "var(--color-text-tertiary)" }}
            >
              K2 / D3 · HP{" "}
              <span style={{ color: hpB > 30 ? "var(--color-live)" : "var(--color-negative)" }}>
                {Math.round(hpB)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Spectator footer */}
      <div
        className="font-mono px-4 py-2 flex items-center justify-between text-[10px] tracking-[0.06em]"
        style={{ color: "var(--color-text-tertiary)" }}
      >
        <span className="inline-flex items-center gap-1.5">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
          </svg>
          <span style={{ color: "var(--color-text-secondary)" }}>342 watching</span>
        </span>
        <span>
          SETTLES IN <span style={{ color: "var(--color-live)" }}>~3s</span>
        </span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────────
// Last line of the headline rotates through synonyms for "the payoff."
// Each variant should be a short, punchy action-result that finishes the
// "Connect. Play. ___." rhythm. Keep them similar in cadence to "Win." —
// avoid anything longer than ~10 characters or the cursor jumps too much.
const HERO_WORDS = ["Win.", "Earn.", "Stack.", "Cash out."];

export default function Hero({ onLaunch }: HeroProps) {
  const [hero, heroIdx] = useWordCycle(HERO_WORDS, 2400);

  return (
    <section className="relative bg-noise overflow-hidden">
      <style>{`
        @keyframes hero-word-in {
          0% { opacity: 0; transform: translateY(14px); filter: blur(8px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes hero-pulse-ring {
          0% { transform: scale(0.9); opacity: 0.7; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes hero-cursor-blink {
          0%, 50% { opacity: 1 }
          50.01%, 100% { opacity: 0 }
        }
      `}</style>

      <AmbientBG />

      <div className="relative z-[5] max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-20 md:pb-24 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-12 items-center">
        {/* LEFT */}
        <div>
          {/* Live wagering pill */}
          <div
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full relative"
            style={{
              background: "var(--color-brand-soft)",
              border: "1px solid var(--color-brand-line)",
            }}
          >
            <span className="relative w-2 h-2">
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  background: "var(--color-brand)",
                  boxShadow: "0 0 8px var(--color-brand)",
                }}
              />
              <span
                className="absolute -inset-1 rounded-full"
                style={{
                  border: "1.5px solid var(--color-brand)",
                  animation: "hero-pulse-ring 1.6s ease-out infinite",
                }}
              />
            </span>
            <span
              className="font-mono text-[11px] tracking-[0.18em] font-semibold"
              style={{ color: "var(--color-brand)" }}
            >
              LIVE WAGERING · 31 IN MATCH
            </span>
          </div>

          {/* Headline */}
          <h1
            className="mt-5 font-display font-extrabold tracking-[-0.035em] text-[var(--color-text-primary)] text-[56px] md:text-[72px] lg:text-[88px]"
            style={{ lineHeight: 0.96 }}
          >
            <div>Connect.</div>
            <div>Play.</div>
            <div className="relative inline-block min-w-[280px] md:min-w-[360px]">
              <span
                key={heroIdx}
                className="inline-block"
                style={{
                  background:
                    "linear-gradient(180deg, #ff8a50, #ff6b00 60%, #e8551a)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "hero-word-in 600ms ease-out",
                }}
              >
                {hero}
              </span>
              <span
                className="inline-block ml-1.5 align-middle"
                style={{
                  width: 6,
                  height: "0.78em",
                  background: "var(--color-brand)",
                  transform: "translateY(-0.08em)",
                  animation: "hero-cursor-blink 1s steps(1) infinite",
                }}
              />
            </div>
          </h1>

          {/* Subhead */}
          <p
            className="mt-7 max-w-[480px] text-[16px] leading-[1.55]"
            style={{ color: "var(--color-text-secondary)" }}
          >
            Bet USDT on skill-based games. Every match is settled on-chain on{" "}
            <span style={{ color: "var(--color-text-primary)", fontWeight: 600 }}>
              Solana
            </span>
            .{" "}
            <span style={{ color: "var(--color-text-tertiary)" }}>
              No house edge — just opponents.
            </span>
          </p>

          {/* CTAs */}
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              onClick={onLaunch}
              className="group inline-flex items-center gap-2.5 px-[22px] py-3.5 rounded-[10px] text-[14px] font-bold cursor-pointer transition-all duration-150 hover:scale-[1.02]"
              style={{
                background: "var(--color-brand)",
                color: "#0a0a0b",
                border: "1px solid var(--color-brand)",
                boxShadow:
                  "0 12px 32px rgba(255,107,0,0.3), 0 0 0 1px rgba(255,107,0,0.4) inset",
              }}
            >
              Launch App
              <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={2.5} />
            </button>
            <a
              href="https://solscan.io/account/RAW7dgEuw7NUs8fXd1LgvNddGr1d85Yv8959ATVUwYw"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 px-[22px] py-3.5 rounded-[10px] text-[14px] font-semibold cursor-pointer transition-colors duration-150"
              style={{
                background: "transparent",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-line-emphasis)",
              }}
            >
              View Contract
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* RIGHT — duel card */}
        <div className="relative flex justify-center">
          <div
            className="absolute -inset-10"
            style={{
              background:
                "radial-gradient(circle at center, rgba(255,107,0,0.18), transparent 60%)",
              filter: "blur(20px)",
            }}
          />
          <div className="relative w-full max-w-[460px]">
            {/* "Watching a real match" tag */}
            <div
              className="font-mono absolute -top-3.5 -left-3.5 z-[2] px-2.5 py-1 rounded-md text-[10px] tracking-[0.14em]"
              style={{
                background: "var(--color-canvas)",
                border: "1px solid var(--color-line-emphasis)",
                color: "var(--color-text-secondary)",
              }}
            >
              ◉ WATCHING A REAL MATCH
            </div>
            <DuelCard />
            {/* Floating settled callout */}
            <div
              className="font-mono hidden md:flex absolute -bottom-3 -right-7 z-[2] px-3 py-2 rounded-lg items-center gap-2 text-[10px] tracking-[0.06em]"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-line-emphasis)",
                color: "var(--color-text-secondary)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--color-live)" }}
              />
              SETTLED ON-CHAIN · 5gW9…7e3b
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
